import React, { useState, useEffect, useRef, useCallback } from 'react';
import Select from 'react-select';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useUser } from '../context/UserContext';
import { fireAudit } from '../utils/auditClient';

const BulkBookingPayment = () => {
  const today = new Date().toISOString().split('T')[0];
  const { userData, selectedCenter } = useUser();
  const userId = userData?.userId;
  const token = localStorage.getItem('auth-token');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    date: today,      
    academy: null,
    sport: null,
    name: '',
    phone: '',
    receivedAmount: '',
    venueQr: '',
    cash: '',
    razorpay: '',
    comments: ''
  });

  const [academies, setAcademies] = useState([]);
  const [academyOptions, setAcademyOptions] = useState([]);
  const [sportsOptions, setSportsOptions] = useState([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  const baselineRef = useRef(null);
  const initializedBaselineRef = useRef(false);

  const buildSubmitPayload = useCallback(() => {
    const toInt = (val) => (val === '' ? 0 : parseInt(val, 10) || 0);
    const venueQr = toInt(String(formData.venueQr));
    const cash = toInt(String(formData.cash));
    const razorpay = toInt(String(formData.razorpay));

    return {
      academyId: formData.academy?.value ?? null,
      sportId: formData.sport?.value ?? 1,
      userId,
      mobileNumber: (formData.phone || '').replace(/\D/g, '').slice(0, 10),
      name: (formData.name || '').trim(),
      bookingDetails: (formData.comments || '').trim(),
      amountReceived: venueQr + cash + razorpay,
      venueQr,
      cash,
      razorpay,
      isEnabled: true,
      date: formData.date,
    };
  }, [formData, userId]);

  const redactForAudit = useCallback((payload) => {
    if (!payload) return payload;
    const copy = JSON.parse(JSON.stringify(payload));
    if (copy.mobileNumber) copy.mobileNumber = '********';
    if (copy.bookingDetails) copy.bookingDetails = '********';
    return copy;
  }, []);

  useEffect(() => {
    if (initializedBaselineRef.current) return;
    if (!optionsLoaded) return;
    baselineRef.current = buildSubmitPayload();
    initializedBaselineRef.current = true;
  }, [optionsLoaded, buildSubmitPayload]);

  const onlyDigits = (val) => (val === '' ? '' : /^\d+$/.test(val) ? val : null);
  const toInt = (val) => (val === '' ? 0 : parseInt(val, 10) || 0);

  useEffect(() => {
    const fetchAcademies = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_API_URL_ACADEMIES, {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        });

        const fetched = response?.data?.data?.academies || [];
        const filtered =
          userData?.userType === 'ACADEMY' && Array.isArray(userData.academyIds)
            ? fetched.filter(a => userData.academyIds.includes(a.id))
            : fetched;

        setAcademies(filtered);
        setAcademyOptions(filtered.map(a => ({ label: a.name, value: a.id })));
        setOptionsLoaded(true);
      } catch (error) {
        console.error('Error fetching academies:', error);
      }
    };

    fetchAcademies();
  }, [userData?.userType, userData?.academyIds]);

  useEffect(() => {
    if (!selectedCenter || academies.length === 0) return;
    const selected = academies.find(a => a.id === Number(selectedCenter));
    if (!selected) return;

    const academyOpt = { label: selected.name, value: selected.id };
    setFormData(prev => ({ ...prev, academy: academyOpt }));

    const sports = (selected.sports || []).map(s => ({
      label: s.name,
      value: s.sport_id,
      image: s.sport_pic
    }));
    setSportsOptions(sports);
  }, [selectedCenter, academies]);

  const handleAcademyChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      academy: selectedOption,
      sport: null
    }));

    const selected = academies.find(a => a.id === selectedOption?.value);
    const sports = (selected?.sports || []).map(s => ({
      label: s.name,
      value: s.sport_id,
      image: s.sport_pic
    }));
    setSportsOptions(sports);

    baselineRef.current = null;
    initializedBaselineRef.current = false;
  };

  const handleSportChange = (selectedOption) => {
    setFormData(prev => ({ ...prev, sport: selectedOption }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, phone: digits }));
      return;
    }

    if (['venueQr', 'cash', 'razorpay'].includes(name)) {
      const v = onlyDigits(value);
      if (v !== null) setFormData(prev => ({ ...prev, [name]: v === '' ? '' : parseInt(v, 10) }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const { academy, venueQr, cash, razorpay, name, phone, comments, sport } = formData;

    if (!academy) {
      Swal.fire({ icon: 'error', title: 'Academy Not Selected!', text: 'Please select an academy.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }
    if (!comments || comments.trim() === '') {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Booking Details are required!', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }
    if (!name || name.trim() === '') {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Name Not Filled!', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }
    if (!phone || String(phone).trim() === '') {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Phone number is required.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }

    const phoneDigits = String(phone).replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Phone number must be exactly 10 digits.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }

    const total = toInt(String(venueQr)) + toInt(String(cash)) + toInt(String(razorpay));
    if (total <= 0) {
      Swal.fire({ icon: 'error', title: 'Payment Missing!', text: 'Enter at least one payment (Venue QR, Cash, or Razorpay).', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }

    const payload = {
      academyId: academy.value,
      sportId: sport?.value ?? 1,
      userId,
      mobileNumber: phoneDigits,
      name: name.trim(),
      bookingDetails: comments.trim(),
      amountReceived: total,
      venueQr: toInt(String(venueQr)),
      cash: toInt(String(cash)),
      razorpay: toInt(String(razorpay)),
      isEnabled: true
    };

    const newPayload = buildSubmitPayload();
    const oldPayload = baselineRef.current || {};

    try {
      setIsSubmitting(true);

      const response = await axios.post(
        process.env.REACT_APP_API_URL_BULK_BOOKING_PAYMET, 
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Bulk Booking Payment entry added successfully.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });

      const affected_id =
        response?.data?.data?.id ??
        response?.data?.id ??
        `${payload.academyId}-${payload.mobileNumber}-${Date.now()}`;

      fireAudit({
        entity: 'bulk_booking_payment',
        affected_id,
        action: 'create',
        businessApi: process.env.REACT_APP_API_URL_BULK_BOOKING_PAYMET,
        businessMethod: 'POST',
        oldPayload: oldPayload,
        newPayload: redactForAudit(newPayload),
        section: 'Centre Management',
        subSection: 'Walkin Sales(bulk booking payment)',
        extra: { page: 'BulkBookingPayment', academyId: payload.academyId, sportId: payload.sportId },
      });

      baselineRef.current = newPayload;

      setFormData(prev => ({
        ...prev,
        name: '',
        phone: '',
        venueQr: '',
        cash: '',
        razorpay: '',
        comments: ''
      }));
    } catch (error) {
      console.error('API Error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error?.response?.data?.message || 'Failed to submit bulk booking payment.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ margin: 'auto' }}>
      <form onSubmit={handleSubmit}>
        {/* Academy */}
        <div className="floating-select-form" style={{ width: '100%' }}>
          <Select
            name="academy"
            options={academyOptions}
            value={formData.academy}
            onChange={handleAcademyChange}
            placeholder="Select Academy"
            className={`walkin-form-dropdown ${formData.academy ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            isClearable
            styles={{
              control: (p) => ({ ...p, padding: '5px' }),
              placeholder: (p) => ({ ...p, color: 'black' }),
            }}
          />
          {formData.academy && <label htmlFor="academy">Academy</label>}
        </div>

        {/* (Optional) Sport selector — uncomment if you want it visible
        <div className="floating-select-form" style={{ width: '100%' }}>
          <Select
            name="sport"
            options={sportsOptions}
            value={formData.sport}
            onChange={selected => setFormData(prev => ({ ...prev, sport: selected }))}
            placeholder="Select Sport"
            className={`walkin-form-dropdown ${formData.sport ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            isClearable
            styles={{
              control: (p) => ({ ...p, padding: '5px' }),
              placeholder: (p) => ({ ...p, color: 'black' }),
            }}
          />
          {formData.sport && <label htmlFor="sport">Sport</label>}
        </div>
        */}

        {/* Name */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`walkin-form ${formData.name ? 'has-value' : ''}`}
            style={{ padding: '12px 10px' }}
            placeholder="Name"
            autoComplete="off"
          />
          {formData.name && <label htmlFor="name">Name</label>}
        </div>

        {/* Phone */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`walkin-form ${formData.phone ? 'has-value' : ''}`}
            style={{ padding: '12px 10px' }}
            placeholder="Phone Number"
            inputMode="numeric"
            pattern="\d{10}"
            maxLength={10}
            autoComplete="off"
          />
          {formData.phone && <label htmlFor="phone">Phone Number</label>}
        </div>

        {/* Amounts */}
        <div>
          <label style={{ margin: 0 }}>Amount Received:</label>

          <div className="floating-select-form" style={{ width: '98%' }}>
            <input
              type="text"
              name="venueQr"
              value={formData.venueQr === 0 ? '0' : formData.venueQr || ''}
              onChange={handleChange}
              className={`walkin-form ${formData.venueQr !== '' ? 'has-value' : ''}`}
              style={{ padding: '12px 10px' }}
              placeholder="Venue QR (₹)"
              inputMode="numeric"
            />
            {formData.venueQr !== '' && <label htmlFor="venueQr">Venue QR</label>}
          </div>

          <div className="floating-select-form" style={{ width: '98%' }}>
            <input
              type="text"
              name="cash"
              value={formData.cash === 0 ? '0' : formData.cash || ''}
              onChange={handleChange}
              className={`walkin-form ${formData.cash !== '' ? 'has-value' : ''}`}
              style={{ padding: '12px 10px' }}
              placeholder="Cash (₹)"
              inputMode="numeric"
            />
            {formData.cash !== '' && <label htmlFor="cash">Cash</label>}
          </div>

          <div className="floating-select-form" style={{ width: '98%' }}>
            <input
              type="text"
              name="razorpay"
              value={formData.razorpay === 0 ? '0' : formData.razorpay || ''}
              onChange={handleChange}
              className={`walkin-form ${formData.razorpay !== '' ? 'has-value' : ''}`}
              style={{ padding: '12px 10px' }}
              placeholder="Razorpay (₹)"
              inputMode="numeric"
            />
            {formData.razorpay !== '' && <label htmlFor="razorpay">Razorpay</label>}
          </div>
        </div>

        {/* Booking details */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <textarea
            name="comments"
            value={formData.comments}
            onChange={handleChange}
            className={`walkin-form ${formData.comments ? 'has-value' : ''}`}
            style={{ padding: '12px 10px' }}
            placeholder="Booking Details"
            maxLength={300}
          />
          {formData.comments && <label htmlFor="comments">Booking Details</label>}
        </div>

        <button className="submit-button" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Submitting…' : 'Submit'}
        </button>
      </form>
    </div>
  );
};

export default BulkBookingPayment;
